<template>
  <Context
    ref="context"
    class="select"
    max-height-if-outside-viewport
    @shown="shown"
  >
    <div class="select__search">
      <i class="select__search-icon iconoir-search"></i>
      <input
        v-model="query"
        type="text"
        class="select__search-input"
        :placeholder="$t('viewsContext.searchView')"
      />
    </div>
    <div v-if="isLoading" class="context--loading">
      <div class="loading"></div>
    </div>
    <ul
      ref="dropdown"
      v-auto-overflow-scroll
      class="select__items select__items--no-max-height"
    >
      <li
        v-for="type in activeViewOwnershipTypes"
        :key="type.getType() + 'group'"
      >
        <div
          v-if="viewsByOwnership(views, type.getType()).length > 0"
          :key="type.getType()"
          class="select__item-label"
        >
          {{ type.getName() }}
        </div>
        <template v-if="viewsByOwnership(views, type.getType()).length > 0">
          <ul
            v-show="
              unsectionedViews(type.getType()).length > 0 || dragKind === 'view'
            "
            class="view-sections__views"
            :class="{
              'view-sections__views--target':
                dragKind === 'view' &&
                unsectionedViews(type.getType()).length === 0 &&
                draggedViewMatches(type.getType()),
            }"
            @dragover.prevent="onViewListDragOver($event, type.getType(), null)"
            @drop.prevent="onViewDrop"
          >
            <ViewsContextItem
              v-for="(view, index) in unsectionedViews(type.getType())"
              :ref="'view-' + view.id"
              :key="view.id"
              :database="database"
              :view="view"
              :table="table"
              :read-only="readOnly"
              :store-prefix="storePrefix"
              :draggable="canReorder"
              :dragging="dragKind === 'view' && draggingId === view.id"
              :drag-kind="dragKind"
              :drop-position="dropMark(type.getType(), null, index)"
              @selected="selectedView"
              @drag-start="onViewDragStart"
              @drag-end="clearDrag"
              @drag-over="onViewItemDragOver($event, type.getType(), null, index)"
              @drop="onViewDrop"
            ></ViewsContextItem>
          </ul>
          <div
            v-for="section in visibleSections(type.getType())"
            :key="section.id"
            class="view-sections"
            :class="{
              ...sectionDropClass(type.getType(), section),
              'view-sections--dragging':
                dragKind === 'section' && draggingId === section.id,
            }"
            @dragover.prevent="onSectionDragOver($event, type.getType(), section)"
            @drop.prevent="onSectionDrop"
          >
            <div class="view-sections__header">
              <button
                type="button"
                class="view-sections__toggle"
                draggable="false"
                :aria-expanded="!isCollapsed(section.id)"
                @click="toggleSection(section.id)"
              >
                <i
                  :class="
                    isCollapsed(section.id)
                      ? 'iconoir-nav-arrow-right'
                      : 'iconoir-nav-arrow-down'
                  "
                ></i>
              </button>
              <input
                v-if="renamingSectionId === section.id"
                ref="sectionNameInput"
                v-model="renamingSectionName"
                class="view-sections__name-input"
                type="text"
                draggable="false"
                @click.stop
                @keydown.enter.prevent="saveSectionName(section)"
                @keydown.esc.prevent="cancelSectionRename"
                @blur="saveSectionName(section)"
              />
              <button
                v-else
                type="button"
                class="view-sections__name"
                :draggable="canReorder"
                @click="startSectionRename(section)"
                @dragstart.stop="onSectionDragStart($event, section)"
                @dragend="clearDrag"
              >
                {{ section.name }}
              </button>
              <button
                v-if="canReorder"
                type="button"
                class="view-sections__delete"
                draggable="false"
                :title="$t('viewsContext.deleteSection')"
                @click="deleteSection(section)"
              >
                <i class="iconoir-bin"></i>
              </button>
            </div>
            <ul
              v-show="!isCollapsed(section.id)"
              class="view-sections__views"
              :class="{
                'view-sections__views--target':
                  dragKind === 'view' &&
                  viewsInSection(type.getType(), section.id).length === 0 &&
                  draggedViewMatches(type.getType()),
              }"
              @dragover.prevent="
                onViewListDragOver($event, type.getType(), section.id)
              "
              @drop.prevent="onViewDrop"
            >
              <ViewsContextItem
                v-for="(view, index) in viewsInSection(
                  type.getType(),
                  section.id
                )"
                :ref="'view-' + view.id"
                :key="view.id"
                :database="database"
                :view="view"
                :table="table"
                :read-only="readOnly"
                :store-prefix="storePrefix"
                :draggable="canReorder"
                :dragging="dragKind === 'view' && draggingId === view.id"
                :drag-kind="dragKind"
                :drop-position="dropMark(type.getType(), section.id, index)"
                @selected="selectedView"
                @drag-start="onViewDragStart"
                @drag-end="clearDrag"
                @drag-over="
                  onViewItemDragOver($event, type.getType(), section.id, index)
                "
                @drop="onViewDrop"
              ></ViewsContextItem>
            </ul>
          </div>
          <div v-if="canReorder && !query" class="view-sections__add">
            <ButtonText
              icon="iconoir-plus"
              @click="addSection(type.getType())"
            >
              {{ $t('viewsContext.addSection') }}
            </ButtonText>
          </div>
        </template>
      </li>
    </ul>
    <div v-if="!isLoading && views.length == 0" class="context__description">
      {{ $t('viewsContext.noViews') }}
    </div>
    <div
      v-if="!readOnly && availableViewOwnershipTypesForCreation.length > 0"
      class="select__footer"
    >
      <div class="select__footer-create">
        <CreateViewLink
          v-for="(viewType, type) in viewTypes"
          :key="type"
          :database="database"
          :table="table"
          :view-type="viewType"
          @created="selectedView"
        ></CreateViewLink>
      </div>
    </div>
  </Context>
</template>

<script>
import { mapGetters, mapState } from 'vuex'

import { notifyIf } from '@onedesk/modules/core/utils/error'
import { escapeRegExp } from '@onedesk/modules/core/utils/string'
import context from '@onedesk/modules/core/mixins/context'
import dropdownHelpers from '@onedesk/modules/core/mixins/dropdownHelpers'
import ViewsContextItem from '@onedesk/modules/database/components/view/ViewsContextItem'
import CreateViewLink from '@onedesk/modules/database/components/view/CreateViewLink'

export default {
  name: 'ViewsContext',
  components: {
    ViewsContextItem,
    CreateViewLink,
  },
  mixins: [context, dropdownHelpers],
  props: {
    database: {
      type: Object,
      required: true,
    },
    table: {
      type: Object,
      required: true,
    },
    views: {
      type: Array,
      required: true,
    },
    readOnly: {
      type: Boolean,
      required: false,
      default: true,
    },
    storePrefix: {
      type: String,
      required: true,
    },
  },
  emits: ['selected-view'],
  data() {
    return {
      query: '',
      firstShow: true,
      collapsedSectionIds: [],
      renamingSectionId: null,
      renamingSectionName: '',
      dragKind: null,
      draggingId: null,
      dropTarget: null,
      sectionDragged: false,
    }
  },
  computed: {
    viewTypes() {
      return this.$registry.getAll('view')
    },
    selectedViews() {
      return this.views.filter((item) => item._.selected)
    },
    ...mapState({
      isLoading: (state) => state.view.loading,
      isLoaded: (state) => state.view.loaded,
    }),
    viewOwnershipTypes() {
      return this.$registry.getAll('viewOwnershipType')
    },
    availableViewOwnershipTypesForCreation() {
      return this.activeViewOwnershipTypes.filter((t) =>
        t.userCanTryCreate(this.table, this.database.workspace.id)
      )
    },
    activeViewOwnershipTypes() {
      return Object.values(this.viewOwnershipTypes).sort(
        (a, b) => a.getListViewTypeSort() - b.getListViewTypeSort()
      )
    },
    ...mapGetters({
      sections: 'view/getSections',
    }),
    canReorder() {
      return (
        !this.readOnly &&
        !this.query &&
        this.$hasPermission(
          'database.table.order_views',
          this.table,
          this.database.workspace.id
        )
      )
    },
  },
  watch: {
    'table.id': {
      handler() {
        this.collapsedSectionIds = this.readCollapsedSections()
      },
      immediate: true,
    },
  },
  methods: {
    shown() {
      if (this.firstShow) {
        this.$nextTick(() => {
          this.scrollViewDropdownIfNeeded()
        })
        this.firstShow = false
      }
    },
    selectedView(view) {
      this.hide()
      this.$emit('selected-view', view)
    },
    /*
      If the currently selected view is not visible inside the dropdown we need to
      scroll just enough so that the selected view is visible as the last element
      in the dropdown.
      In case there are no views, we don't need to do anything and can simply return.
    */
    scrollViewDropdownIfNeeded() {
      if (this.selectedViews.length === 0) {
        return
      }
      const dropdownElement = this.$refs.dropdown
      const selectedViewItem = this.getSelectedViewItem()
      const dropdownHeight = dropdownElement.clientHeight
      if (
        this.isSelectedViewOutOfDropdownView(selectedViewItem, dropdownHeight)
      ) {
        dropdownElement.scrollTop = this.calculateOffsetToSelectedViewItem(
          dropdownElement,
          selectedViewItem
        )
      }
    },
    /**
     * This method filters the view elements and returns the currently selected
     * view dom item based on whether or not it is selected.
     */
    getSelectedViewItem() {
      const selectedViewItemID = this.selectedViews[0].id
      return this.$refs[`view-${selectedViewItemID}`][0].$el
    },
    /**
     * This method calculates whether or not the selectedViewItem is fully visible
     * inside the ViewContext dropdown or not
     */
    isSelectedViewOutOfDropdownView(selectedViewItem, dropdownHeight) {
      const selectedOffsetPlusHeight =
        selectedViewItem.offsetTop + selectedViewItem.clientHeight
      return selectedOffsetPlusHeight > dropdownHeight
    },
    /**
     * This method calculates the necessary offsetTop of the dropdown element so that
     * the selected view item is the bottom element.
     */
    calculateOffsetToSelectedViewItem(dropdownElement, selectedViewItem) {
      const {
        parentContainerBeforeHeight,
        itemHeightWithMargins,
        itemsInView,
      } = this.getStyleProperties(dropdownElement, selectedViewItem)

      const viewItemsBeforeSelectedViewItemHeight =
        (itemsInView - 1) * itemHeightWithMargins

      return (
        selectedViewItem.offsetTop -
        viewItemsBeforeSelectedViewItemHeight -
        parentContainerBeforeHeight
      )
    },
    searchAndOrder(views) {
      const query = this.query

      return views
        .filter(function (view) {
          const regex = new RegExp('(' + escapeRegExp(query) + ')', 'i')
          return view.name.match(regex)
        })
        .sort((a, b) => a.order - b.order)
    },
    viewsByOwnership(views, ownershipType) {
      return this.searchAndOrder(views).filter(
        (view) => view.ownership_type === ownershipType
      )
    },
    sectionsFor(ownershipType) {
      return this.sections
        .filter((section) => section.ownership_type === ownershipType)
        .sort((a, b) => a.order - b.order || a.id - b.id)
    },
    knownSectionIds(ownershipType) {
      return new Set(this.sectionsFor(ownershipType).map((section) => section.id))
    },
    unsectionedViews(ownershipType) {
      const known = this.knownSectionIds(ownershipType)
      return this.viewsByOwnership(this.views, ownershipType).filter(
        (view) => !view.section_id || !known.has(view.section_id)
      )
    },
    viewsInSection(ownershipType, sectionId) {
      return this.viewsByOwnership(this.views, ownershipType).filter(
        (view) => view.section_id === sectionId
      )
    },
    visibleSections(ownershipType) {
      return this.sectionsFor(ownershipType).filter((section) => {
        if (!this.query) {
          return true
        }
        return this.viewsInSection(ownershipType, section.id).length > 0
      })
    },
    isCollapsed(sectionId) {
      return this.query ? false : this.collapsedSectionIds.includes(sectionId)
    },
    collapsedStorageKey() {
      return `view-sections-collapsed:${this.table.id}`
    },
    readCollapsedSections() {
      if (!import.meta.client || !this.table?.id) {
        return []
      }
      try {
        const stored = window.localStorage.getItem(this.collapsedStorageKey)
        return stored ? JSON.parse(stored) : []
      } catch {
        return []
      }
    },
    writeCollapsedSections() {
      if (!import.meta.client || !this.table?.id) {
        return
      }
      window.localStorage.setItem(
        this.collapsedStorageKey,
        JSON.stringify(this.collapsedSectionIds)
      )
    },
    toggleSection(sectionId) {
      if (this.isCollapsed(sectionId)) {
        this.collapsedSectionIds = this.collapsedSectionIds.filter(
          (id) => id !== sectionId
        )
      } else {
        this.collapsedSectionIds = [...this.collapsedSectionIds, sectionId]
      }
      this.writeCollapsedSections()
    },
    nextSectionName(ownershipType) {
      const names = new Set(
        this.sectionsFor(ownershipType).map((section) => section.name)
      )
      const base = this.$t('viewsContext.sectionDefaultName')
      if (!names.has(base)) {
        return base
      }
      let index = 2
      while (names.has(`${base} ${index}`)) {
        index += 1
      }
      return `${base} ${index}`
    },
    async addSection(ownershipType) {
      try {
        const section = await this.$store.dispatch('view/createSection', {
          table: this.table,
          name: this.nextSectionName(ownershipType),
          ownershipType,
        })
        this.startSectionRename(section)
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    startSectionRename(section) {
      if (this.sectionDragged) {
        this.sectionDragged = false
        return
      }
      if (!this.canReorder) {
        return
      }
      this.renamingSectionId = section.id
      this.renamingSectionName = section.name
      this.$nextTick(() => {
        const input = this.$refs.sectionNameInput
        const element = Array.isArray(input) ? input[0] : input
        element?.focus()
        element?.select()
      })
    },
    cancelSectionRename() {
      this.renamingSectionId = null
      this.renamingSectionName = ''
    },
    async saveSectionName(section) {
      if (this.renamingSectionId !== section.id) {
        return
      }
      const name = this.renamingSectionName.trim()
      this.renamingSectionId = null
      if (!name || name === section.name) {
        return
      }
      try {
        await this.$store.dispatch('view/updateSection', { section, name })
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    async deleteSection(section) {
      try {
        await this.$store.dispatch('view/deleteSection', section)
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    onViewDragStart(view) {
      this.dragKind = 'view'
      this.draggingId = view.id
      this.dropTarget = null
    },
    onSectionDragStart(event, section) {
      this.sectionDragged = true
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', `section:${section.id}`)
      this.dragKind = 'section'
      this.draggingId = section.id
      this.dropTarget = null
    },
    draggedViewMatches(ownershipType) {
      const view = this.views.find((item) => item.id === this.draggingId)
      return Boolean(view) && view.ownership_type === ownershipType
    },
    clearDrag() {
      this.dragKind = null
      this.draggingId = null
      this.dropTarget = null
    },
    onViewItemDragOver(event, ownershipType, sectionId, index) {
      if (this.dragKind !== 'view' || !this.draggedViewMatches(ownershipType)) {
        return
      }
      const rect = event.currentTarget.getBoundingClientRect()
      const after = event.clientY > rect.top + rect.height / 2
      this.dropTarget = {
        kind: 'view',
        ownershipType,
        sectionId,
        index: after ? index + 1 : index,
      }
    },
    onViewListDragOver(event, ownershipType, sectionId) {
      if (
        this.dragKind !== 'view' ||
        !this.draggedViewMatches(ownershipType) ||
        event.target !== event.currentTarget
      ) {
        return
      }
      const views =
        sectionId == null
          ? this.unsectionedViews(ownershipType)
          : this.viewsInSection(ownershipType, sectionId)
      this.dropTarget = {
        kind: 'view',
        ownershipType,
        sectionId,
        index: views.length,
      }
    },
    onSectionDragOver(event, ownershipType, section) {
      if (this.dragKind === 'view') {
        if (!this.draggedViewMatches(ownershipType)) {
          return
        }
        if (this.isCollapsed(section.id)) {
          this.collapsedSectionIds = this.collapsedSectionIds.filter(
            (id) => id !== section.id
          )
          this.writeCollapsedSections()
        }
        if (
          !event.target.closest('.select__item, .view-sections__views')
        ) {
          this.dropTarget = {
            kind: 'view',
            ownershipType,
            sectionId: section.id,
            index: 0,
          }
        }
        return
      }
      const dragged = this.sections.find((item) => item.id === this.draggingId)
      if (!dragged || dragged.ownership_type !== section.ownership_type) {
        return
      }
      const rect = event.currentTarget.getBoundingClientRect()
      this.dropTarget = {
        kind: 'section',
        ownershipType,
        sectionId: section.id,
        after: event.clientY > rect.top + rect.height / 2,
      }
    },
    dropMark(ownershipType, sectionId, index) {
      const target = this.dropTarget
      if (
        !target ||
        target.kind !== 'view' ||
        target.ownershipType !== ownershipType ||
        target.sectionId !== sectionId
      ) {
        return null
      }
      const views =
        sectionId == null
          ? this.unsectionedViews(ownershipType)
          : this.viewsInSection(ownershipType, sectionId)
      if (target.index === index) {
        return 'before'
      }
      if (index === views.length - 1 && target.index === views.length) {
        return 'after'
      }
      return null
    },
    sectionDropClass(ownershipType, section) {
      const target = this.dropTarget
      if (
        !target ||
        target.kind !== 'section' ||
        target.ownershipType !== ownershipType ||
        target.sectionId !== section.id
      ) {
        return {}
      }
      return {
        'view-sections--drop-before': !target.after,
        'view-sections--drop-after': target.after,
      }
    },
    viewGroups(ownershipType) {
      const views = this.viewsByOwnership(this.views, ownershipType)
      const known = this.knownSectionIds(ownershipType)
      const groups = [
        {
          sectionId: null,
          ids: views
            .filter((view) => !view.section_id || !known.has(view.section_id))
            .map((view) => view.id),
        },
      ]
      this.sectionsFor(ownershipType).forEach((section) => {
        groups.push({
          sectionId: section.id,
          ids: views
            .filter((view) => view.section_id === section.id)
            .map((view) => view.id),
        })
      })
      return groups
    },
    async onViewDrop() {
      const target = this.dropTarget
      const viewId = this.draggingId
      if (
        this.dragKind !== 'view' ||
        !target ||
        target.kind !== 'view' ||
        !this.draggedViewMatches(target.ownershipType)
      ) {
        return
      }
      this.clearDrag()
      await this.moveView(
        target.ownershipType,
        viewId,
        target.sectionId,
        target.index
      )
    },
    async onSectionDrop() {
      const target = this.dropTarget
      const sectionId = this.draggingId
      const kind = this.dragKind
      this.clearDrag()
      if (kind !== 'section' || !target || target.kind !== 'section') {
        return
      }
      if (sectionId === target.sectionId) {
        return
      }
      const oldOrder = this.sectionsFor(target.ownershipType).map(
        (section) => section.id
      )
      const order = [...oldOrder]
      const from = order.indexOf(sectionId)
      order.splice(from, 1)
      let to = order.indexOf(target.sectionId)
      if (from === -1 || to === -1) {
        return
      }
      if (target.after) {
        to += 1
      }
      order.splice(to, 0, sectionId)
      if (order.join(',') === oldOrder.join(',')) {
        return
      }
      try {
        await this.$store.dispatch('view/orderSections', {
          table: this.table,
          ownershipType: target.ownershipType,
          order,
          oldOrder,
        })
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    async moveView(ownershipType, viewId, sectionId, index) {
      const groups = this.viewGroups(ownershipType)
      const oldOrder = groups.flatMap((group) => group.ids)
      let fromSection = null
      let fromIndex = -1
      groups.forEach((group) => {
        const found = group.ids.indexOf(viewId)
        if (found !== -1) {
          fromSection = group.sectionId
          fromIndex = found
          group.ids.splice(found, 1)
        }
      })
      const destination = groups.find((group) => group.sectionId === sectionId)
      if (!destination) {
        return
      }
      let insertAt = index
      if (fromSection === sectionId && fromIndex !== -1 && fromIndex < insertAt) {
        insertAt -= 1
      }
      insertAt = Math.max(0, Math.min(insertAt, destination.ids.length))
      if (fromSection === sectionId && fromIndex === insertAt) {
        return
      }
      destination.ids.splice(insertAt, 0, viewId)
      const order = groups.flatMap((group) => group.ids)
      const view = this.views.find((item) => item.id === viewId)
      const known = this.knownSectionIds(ownershipType)
      const currentSection =
        view.section_id && known.has(view.section_id) ? view.section_id : null
      try {
        if (currentSection !== sectionId) {
          await this.$store.dispatch('view/moveToSection', {
            view,
            sectionId,
          })
        }
        if (oldOrder.join(',') !== order.join(',')) {
          await this.order(ownershipType, order, oldOrder)
        }
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
    async order(ownershipType, order, oldOrder) {
      try {
        await this.$store.dispatch('view/order', {
          table: this.table,
          ownershipType,
          order,
          oldOrder,
        })
      } catch (error) {
        notifyIf(error, 'view')
      }
    },
  },
}
</script>
